<template>
  <div class="kanban-board">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
          ></v-progress-circular>
          <div class="mt-4 text-h6">Carregando projetos...</div>
          <div class="text-caption text--secondary mt-2">Aguarde enquanto os dados são carregados</div>
        </v-card-text>
      </v-card>
    </div>

    <div class="kanban-columns" :class="{ 'loading-blur': isLoading }">
      <div v-for="step in allProjectSteps" :key="step.id" class="kanban-column">
        <div class="column-header" :style="{ backgroundColor: step.color }">
          <div class="d-flex align-center">
            <img
              :src="step.icon"
              alt="Step Icon"
              class="step-icon mr-2"
              style="width: 24px; height: 24px;"
            />
            <h3 class="white--text mb-0">{{ step.name }}</h3>
          </div>
          <span class="white--text">{{ getColumnCount(step.id) }}</span>
        </div>
        <div 
          class="column-content"
          @dragover.prevent="enableDragAndDrop && onDragOver($event)"
          @drop="enableDragAndDrop && onDrop($event, step.id)"
        >
          <div
            v-for="project in getProjectsByStep(step.id)"
            :key="project.id_doc"
            class="project-card"
            :class="{ 
              'draggable': enableDragAndDrop,
              'sla-warning': isSLAWarning(project),
              'sla-expired': isSLAExpired(project)
            }"
            :draggable="enableDragAndDrop"
            @dragstart="enableDragAndDrop && onDragStart($event, project)"
            @click="navigateProject(project)"
          >
            <v-card class="mx-2 my-2" elevation="2" style="position: relative;">
              <!-- Menu fixo no canto superior direito -->
              <div class="project-menu-container" v-if="isAdmin">
                <v-tooltip v-if="isSLAWarning(project) || isSLAExpired(project)" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      :color="isSLAExpired(project) ? 'error' : 'warning'"
                      small
                      class="mr-2"
                    >
                      mdi-clock-alert
                    </v-icon>
                  </template>
                  <span>{{ getSLATooltip(project) }}</span>
                </v-tooltip>
                
                <!-- Menu de ações do projeto -->
                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                      class="ml-1"
                      :disabled="isLoading"
                    >
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <!-- Opção de reativar - apenas para admins e projetos inativos -->
                    <v-list-item 
                      v-if="isAdmin && isProjectInInactiveColumn(project)" 
                      @click="openReactivateDialog(project)" 
                      class="px-3"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="success">mdi-play-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">Reativar Projeto</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    
                    <v-divider v-if="isAdmin && isProjectInInactiveColumn(project)"></v-divider>
                    
                    <!-- Mostra opções de declinar/reprovar apenas para projetos não finalizados -->
                    <template v-if="!isProjectFinished(project)">
                      <v-list-item @click="openDeclineDialog(project)" class="px-3">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="error">mdi-close-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">Declinar Projeto</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <v-list-item @click="openRejectDialog(project)" class="px-3">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="warning">mdi-alert-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">Reprovar Projeto</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <v-divider></v-divider>
                    </template>
                    
                    <v-list-item @click="navigateProject(project)" class="px-3">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="primary">mdi-eye</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">Ver Detalhes</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    
                    <!-- Item de observações - apenas para não-franqueados -->
                    <v-list-item 
                      v-if="!isFranchisee" 
                      @click="openNotesDialog(project)" 
                      class="px-3"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="info">mdi-note-text</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-caption">Observações</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              
              <v-card-title class="text-subtitle-1 pr-8">
                {{ project.project_name }}
              </v-card-title>
              <v-card-text>
                <div class="d-flex flex-column gap-2">
                  <!-- Status Chips -->
                  <div class="d-flex flex-wrap gap-1">
                    <!-- Show franchisee status for franchisee users -->
                    <v-chip
                      v-if="isFranchisee"
                      small
                      :color="getChipColor(project.currentStatusFranchisee, project.lastLogMessage)"
                      text-color="white"
                      class="mr-1"
                    >
                      {{ getChipStatus(project.currentStatusFranchisee, project.lastLogMessage) }}
                    </v-chip>
                    <!-- Show ADM status for non-franchisee users -->
                    <v-chip
                      v-if="!isFranchisee && project.currentStatusADM"
                      small
                      :color="getChipColor(project.currentStatusADM, project.lastLogMessage)"
                      text-color="white"
                    >
                      {{ getChipStatus(project.currentStatusADM, project.lastLogMessage) }}
                    </v-chip>
                  </div>

                  <!-- Tag especial para projetos inativos -->
                  <div v-if="isProjectInInactiveColumn(project)" class="d-flex flex-wrap gap-1">
                    <v-chip
                      small
                      color="grey"
                      text-color="white"
                      class="mr-1"
                    >
                      <v-icon left small>mdi-pause-circle</v-icon>
                      {{ getCurrentStepName(project) }}
                    </v-chip>
                    <v-chip
                      small
                      color="orange"
                      text-color="white"
                    >
                      <v-icon left small>mdi-clock-outline</v-icon>
                      {{ getDaysInactive(project) }} dias inativo
                    </v-chip>
                  </div>

                  <!-- Project Info -->
                  <div class="d-flex align-center justify-space-between">
                    <div class="text-caption">
                      Cliente: {{ project.lead_name }}
                    </div>
                    <div class="d-flex align-center">
                      <v-icon
                        v-if="project.has_message"
                        small
                        color="warning"
                        @click.stop="openMessageDialog(project.id_doc)"
                        class="mr-1"
                      >
                        mdi-chat-alert
                      </v-icon>
                      <v-icon
                        v-else
                        small
                        color="primary"
                        @click.stop="openMessageDialog(project.id_doc)"
                        class="mr-1"
                      >
                        mdi-chat-plus
                      </v-icon>
                      
                      <!-- Indicador de observações (apenas para não-franqueados) -->
                      <v-tooltip v-if="!isFranchisee && project.has_notes" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="info"
                            @click.stop="openNotesDialog(project)"
                            class="mr-1"
                          >
                            mdi-note-text
                          </v-icon>
                        </template>
                        <span>Ver observações do projeto</span>
                      </v-tooltip>
                      <span class="text-caption">
                        {{ formatDate(getLastStatusDate(project)) }}
                      </span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Diálogo de confirmação para ações -->
    <ProjectActionDialog
      v-model="showActionDialog"
      :project="selectedProject"
      :action-type="actionType"
      @confirm="handleProjectAction"
      @error="handleActionError"
    />

    <!-- Diálogo de observações do projeto -->
    <ProjectNotesDialog
      v-model="showNotesDialog"
      :project="selectedProjectForNotes"
      @close="showNotesDialog = false"
    />
  </div>
</template>

<script>
import UtilsProjects from "@/pages/Projects/Utils/Utils.js";
import { StatusReference } from "@/pages/Projects/Utils/StatusConstants.js";
import { getSpecialColumns, getSpecialColumnConfig } from "./KanbanConfig.js";
import ProjectActionDialog from "./ProjectActionDialog.vue";
import ProjectNotesDialog from "./ProjectNotesDialog.vue";
import { showErrorSnackBar, showInfoSnackBar, showSuccessSnackBar } from "@/utils/SnackBartHelper"
import { mapGetters } from 'vuex';
import ProjectsService from "@/services/server/ProjectsService.js";

export default {
  name: 'ProjectKanban',
  
  components: {
    ProjectActionDialog,
    ProjectNotesDialog
  },
  
  props: {
    projects: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    getChipColor: {
      type: Function,
      required: true
    },
    getChipStatus: {
      type: Function,
      required: true
    },
    formatDate: {
      type: Function,
      required: true
    },
    isFranchisee: {
      type: Boolean,
      required: true
    },
    enableDragAndDrop: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    draggedProject: null,
    showActionDialog: false,
    selectedProject: null,
    actionType: 'decline',
    localLoading: false, // Propriedade local para gerenciar loading
    showNotesDialog: false,
    selectedProjectForNotes: null,
    // Colunas principais do fluxo de trabalho
    projectSteps: [
      {
        id: UtilsProjects.STEPS.PROSPECTING,
        name: 'Prospecção',
        icon: require("@/assets/projects/fz_magnify.png"),
        color: '#1976d2'
      },
      {
        id: UtilsProjects.STEPS.DIAGNOSTIC,
        name: 'Waterscan',
        icon: require("@/assets/projects/WaterScan.png"),
        color: '#1976d2'
      },
      {
        id: UtilsProjects.STEPS.APPROVAL,
        name: 'Aprovação',
        icon: require("@/assets/projects/fz_approval.png"),
        color: 'rgb(24, 43, 87)'
      },
      {
        id: UtilsProjects.STEPS.CLOSING,
        name: 'Fechamento',
        icon: require("@/assets/projects/fz_deal.png"),
        color: '#1976d2'
      },
      {
        id: UtilsProjects.STEPS.CONTRACTS,
        name: 'Contratos',
        icon: require("@/assets/projects/fz_docs.png"),
        color: 'rgb(24, 43, 87)'
      },
      {
        id: UtilsProjects.STEPS.MATERIALS,
        name: 'Entrega',
        icon: require("@/assets/projects/fz_shipping.png"),
        color: '#2196F3'
      },
      {
        id: UtilsProjects.STEPS.INSTALLATION,
        name: 'Waterfix',
        icon: require("@/assets/projects/WaterFix.png"),
        color: '#2196F3'
      },
      {
        id: UtilsProjects.STEPS.FINISH,
        name: 'Finalizado',
        icon: require("@/assets/projects/Finalizado.png"),
        color: 'rgb(24, 43, 87)'
      }
    ]
  }),

  computed: {
    ...mapGetters([
      "getCurrentUserRank"
    ]),
    // Combina as colunas principais com as especiais
    allProjectSteps() {
      return [...this.projectSteps, ...getSpecialColumns()];
    },
    
    // Computed property para combinar loading do prop com loading local
    isLoading() {
      return this.loading || this.localLoading;
    },

    // Verifica se o usuário atual é administrador
    isAdmin() {
      return this.getCurrentUserRank === "Super Admin" || this.getCurrentUserRank === "Admin";
    }
  },

  methods: {
    getColumnCount(stepId) {
      return this.getProjectsByStep(stepId).length
    },

    getProjectsByStep(stepId) {
      // Se for uma coluna especial, usa lógica específica
      if (this.isSpecialColumn(stepId)) {
        return this.getProjectsBySpecialColumn(stepId);
      }

      // Lógica original para colunas principais
      return this.projects
        .filter(project => {
          // Primeiro verifica se o projeto está em alguma coluna especial
          if (this.isProjectInSpecialColumn(project)) {
            return false; // Não mostra na coluna principal se está em coluna especial
          }
          
          const currentStep = project.steps.find(step => step.isCurrentStep === true);
          return currentStep && currentStep.name === stepId;
        })
        .sort((a, b) => {
          const dateA = this.isFranchisee ? a.currentHourStatusFranchisee : a.currentHourStatusADM;
          const dateB = this.isFranchisee ? b.currentHourStatusFranchisee : b.currentHourStatusADM;
          
          // Se não houver data de status, usa a data de criação
          const timestampA = dateA ? new Date(dateA).getTime() : new Date(a.created_at_formatted).getTime();
          const timestampB = dateB ? new Date(dateB).getTime() : new Date(b.created_at_formatted).getTime();
          
          return timestampA - timestampB; // Ordem crescente (mais antigo para mais novo)
        });
    },

    // Verifica se é uma coluna especial
    isSpecialColumn(stepId) {
      const specialColumns = getSpecialColumns();
      return specialColumns.some(col => col.id === stepId);
    },

    // Verifica se um projeto está em alguma coluna especial
    isProjectInSpecialColumn(project) {
      const specialColumns = getSpecialColumns();
      
      for (const column of specialColumns) {
        const columnConfig = getSpecialColumnConfig(column.id);
        if (columnConfig && columnConfig.filterFunction) {
          if (columnConfig.filterFunction(project, this.isFranchisee)) {
            return true; // Projeto está em uma coluna especial
          }
        }
      }
      
      return false; // Projeto não está em nenhuma coluna especial
    },

    // Lógica para filtrar projetos nas colunas especiais
    getProjectsBySpecialColumn(stepId) {
      const columnConfig = getSpecialColumnConfig(stepId);
      if (!columnConfig || !columnConfig.filterFunction) {
        return [];
      }

      return this.projects
        .filter(project => columnConfig.filterFunction(project, this.isFranchisee))
        .sort((a, b) => {
          const dateA = this.isFranchisee ? a.currentHourStatusFranchisee : a.currentHourStatusADM;
          const dateB = this.isFranchisee ? b.currentHourStatusFranchisee : b.currentHourStatusADM;
          
          const timestampA = dateA ? new Date(dateA).getTime() : new Date(a.created_at_formatted).getTime();
          const timestampB = dateB ? new Date(dateB).getTime() : new Date(b.created_at_formatted).getTime();
          
          return timestampA - timestampB;
        });
    },

    onDragOver(event) {
      event.preventDefault();
    },

    onDragStart(event, project) {
      if (!this.enableDragAndDrop) return;
      this.draggedProject = project;
      event.dataTransfer.effectAllowed = 'move';
    },

    onDrop(event, stepId) {
      if (!this.enableDragAndDrop || !this.draggedProject) return;
      
      this.$emit('step-change', {
        projectId: this.draggedProject.id_doc,
        newStep: stepId
      });
      this.draggedProject = null;
    },

    navigateProject(project) {
      this.$emit('navigate', project);
    },

    openMessageDialog(projectId) {
      this.$emit('open-message', projectId);
    },

    openNotesDialog(project) {
      this.selectedProjectForNotes = project;
      this.showNotesDialog = true;
    },

    // Métodos para abrir diálogos de ação
    openDeclineDialog(project) {
      this.selectedProject = project;
      this.actionType = 'decline';
      this.showActionDialog = true;
    },

    openRejectDialog(project) {
      this.selectedProject = project;
      this.actionType = 'reject';
      this.showActionDialog = true;
    },

    openReactivateDialog(project) {
      this.selectedProject = project;
      this.actionType = 'reactivate';
      this.showActionDialog = true;
    },

    // Método para lidar com a confirmação da ação
    handleActionConfirm(actionData) {
      // console.log("actionData",actionData)

      // Emite o evento para o componente pai lidar com a ação
      // this.$emit('project-action', actionData);
    },
    
    async addStautsProject(id_project, status, statusAdm = null) {
            await UtilsProjects.addStatusProject(id_project, UtilsProjects.STEPS.OUT, status, UtilsProjects.STEPS.OUT)
            await UtilsProjects.addStatusProject(id_project, UtilsProjects.STEPS.OUT, statusAdm ? statusAdm : status, UtilsProjects.STEPS.OUT, false)
        },
    async handleProjectAction(actionData) {
      try {
        this.localLoading = true;
        const { projectId, actionType, reason, project } = actionData;
        
        // Determina o status baseado na ação
        if (actionType === 'decline') {
          // Atualiza o status do projeto
          await this.addStautsProject(projectId, 2002, 2001);
          actionData.project.currentStatusFranchisee = 2002;
          actionData.project.currentStatusADM = 2001;
        } else if (actionType === 'reject') {
          // Atualiza o status do projeto
          await this.addStautsProject(projectId, 2004, 2003);
          actionData.project.currentStatusFranchisee = 2004;
          actionData.project.currentStatusADM = 2003;
        } else if (actionType === 'reactivate') {
          // Reativa o projeto atualizando as datas de status
          await this.reactivateProject(projectId, project);
        }
        
        // // Emite evento para atualizar a lista de projetos
        this.$emit('update-projects',actionData);
        
        // Mostra feedback
        let successMessage = '';
        if (actionType === 'decline') {
          successMessage = 'Projeto declinado com sucesso!';
        } else if (actionType === 'reject') {
          successMessage = 'Projeto reprovado com sucesso!';
        } else if (actionType === 'reactivate') {
          const stepName = this.getCurrentStepName(project);
          successMessage = `Projeto reativado com sucesso! Retornou para o passo "${stepName}".`;
        }
        
        if (successMessage) {
          showSuccessSnackBar(successMessage);
        }
        
      } catch (error) {
        console.error('Erro ao executar ação:', error);
        showErrorSnackBar('Erro ao executar ação. Tente novamente.');
      } finally {
        this.localLoading = false;
      }
    },
    // Método para lidar com erros
    handleActionError(error) {
      console.error('Erro na ação do projeto:', error);
      this.$emit('action-error', error);
    },

    getStatusInfo(project) {
      const status = this.isFranchisee ? project.currentStatusFranchisee : project.currentStatusADM;
      return StatusReference[status] || null;
    },

    isSLAWarning(project) {
      const statusInfo = this.getStatusInfo(project);
      if (!statusInfo?.slaHours) return false;

      const statusHour = this.isFranchisee ? project.currentHourStatusFranchisee : project.currentHourStatusADM;
      if (!statusHour) return false;

      const statusDate = new Date(statusHour);
      const now = new Date();
      const hoursElapsed = (now - statusDate) / (1000 * 60 * 60);
      const hoursRemaining = statusInfo.slaHours - hoursElapsed;

      // Warning if less than 25% of SLA time remains
      return hoursRemaining > 0 && hoursRemaining <= (statusInfo.slaHours * 0.25);
    },

    isSLAExpired(project) {
      const statusInfo = this.getStatusInfo(project);
      if (!statusInfo?.slaHours) return false;

      const statusHour = this.isFranchisee ? project.currentHourStatusFranchisee : project.currentHourStatusADM;
      if (!statusHour) return false;

      const statusDate = new Date(statusHour);
      const now = new Date();
      const hoursElapsed = (now - statusDate) / (1000 * 60 * 60);

      return hoursElapsed > statusInfo.slaHours;
    },

    getSLATooltip(project) {
      const statusInfo = this.getStatusInfo(project);
      if (!statusInfo?.slaHours) return '';

      const statusHour = this.isFranchisee ? project.currentHourStatusFranchisee : project.currentHourStatusADM;
      if (!statusHour) return '';

      const statusDate = new Date(statusHour);
      const now = new Date();
      const hoursElapsed = (now - statusDate) / (1000 * 60 * 60);
      const hoursRemaining = statusInfo.slaHours - hoursElapsed;

      if (hoursRemaining <= 0) {
        return `Expirado há ${Math.abs(Math.round(hoursRemaining))} horas`;
      } else {
        return `Expira em ${Math.round(hoursRemaining)} horas`;
      }
    },

    getLastStatusDate(project) {
      const statusHour = this.isFranchisee ? project.currentHourStatusFranchisee : project.currentHourStatusADM;
      return statusHour || project.created_at_formatted; // fallback para data de criação se não houver status
    },

    // Verifica se o projeto está na coluna de inativos
    isProjectInInactiveColumn(project) {
      try {
        const inactiveColumnConfig = getSpecialColumnConfig('inativos');
        
        if (!inactiveColumnConfig || !inactiveColumnConfig.filterFunction) {
          return false;
        }
        
        const result = inactiveColumnConfig.filterFunction(project, this.isFranchisee);
        return result;
      } catch (error) {
        console.error('Erro ao verificar se projeto está inativo:', error);
        return false;
      }
    },

    // Obtém o nome do passo atual do projeto
    getCurrentStepName(project) {
      const currentStep = project.steps?.find(step => step.isCurrentStep === true);
      if (!currentStep) return 'N/A';
      
      // Mapeia os nomes dos steps para nomes mais amigáveis
      const stepNames = {
        'prospecting': 'Prospecção',
        'diagnostic': 'Waterscan',
        'approval': 'Aprovação',
        'closing': 'Fechamento',
        'contracts': 'Contratos',
        'materials': 'Entrega',
        'installation': 'Waterfix',
        'finish': 'Finalizado'
      };
      
      return stepNames[currentStep.name] || currentStep.name;
    },

    // Calcula quantos dias o projeto está inativo
    getDaysInactive(project) {
      const lastActivity = this.isFranchisee ? project.currentHourStatusFranchisee : project.currentHourStatusADM;
      const lastActivityDate = new Date(lastActivity || project.created_at_formatted);
      const now = new Date();
      const daysInactive = Math.floor((now - lastActivityDate) / (1000 * 60 * 60 * 24));
      
      return daysInactive;
    },

    // Verifica se o projeto está finalizado
    isProjectFinished(project) {
      const currentStep = project.steps?.find(step => step.isCurrentStep === true);
      return currentStep && currentStep.name === 'finish';
    },

    // Reativa um projeto atualizando as datas de status
    async reactivateProject(projectId, project) {
      try {
        const now = new Date();
        const currentStep = project.steps?.find(step => step.isCurrentStep === true);
        const stepName = currentStep ? currentStep.name : UtilsProjects.STEPS.OUT;
        
        // Atualiza as datas de status para ambos (franqueado e ADM)
        await ProjectsService.updateProject(projectId, {
          currentHourStatusFranchisee: now,
          currentHourStatusADM: now
        });
        
        // Atualiza as datas no objeto local do projeto
        project.currentHourStatusFranchisee = now;
        project.currentHourStatusADM = now;
        
        // Cria log de reativação
        const stepNameFormatted = this.getCurrentStepName(project);
        await UtilsProjects.createSuccessLog(
          projectId,
          stepName,
          `Projeto reativado para o passo "${stepNameFormatted}"`,
          stepName
        );
        
        // Atualiza as mensagens de log para ambos os lados
        const logMessage = `Projeto reativado para o passo "${stepNameFormatted}" - ${new Date().toLocaleString('pt-BR')}`;
        await ProjectsService.updateProject(projectId, {
          lastLogMessageFranchisee: logMessage,
          lastLogMessageADM: logMessage
        });
        
      } catch (error) {
        console.error('Erro ao reativar projeto:', error);
        throw error;
      }
    }
  }
}
</script>

<style scoped>
.kanban-board {
  padding: 16px;
  height: calc(100vh - 300px);
  overflow-x: auto;
  display: flex;
  flex-direction: column;
}

.kanban-columns {
  display: flex;
  gap: 16px;
  height: 100%;
  min-width: min-content;
  overflow-y: hidden; /* Remove scroll vertical */
}

.kanban-column {
  min-width: 320px;
  width: 320px;
  background-color: #f5f5f5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 100%; /* Garante que a coluna ocupe toda a altura disponível */
}

.column-header {
  padding: 12px;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #1976D2;
}

.column-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}

.column-content {
  flex: 1;
  padding: 8px;
  overflow-y: auto; /* Permite scroll apenas no conteúdo da coluna */
  min-height: 0; /* Importante para o flex funcionar corretamente */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.project-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.project-card.draggable {
  cursor: grab;
}

.project-card.draggable:hover {
  transform: translateY(-2px);
}

.project-card.draggable:active {
  cursor: grabbing;
}

.step-icon {
  object-fit: contain;
}

/* Custom scrollbar for columns */
.column-content::-webkit-scrollbar {
  width: 6px;
  display: none; /* Hide scrollbar by default */
}

.column-content:hover::-webkit-scrollbar {
  display: block; /* Show scrollbar on hover */
}

.column-content:hover {
  scrollbar-width: thin; /* Show scrollbar on hover for Firefox */
  -ms-overflow-style: auto; /* Show scrollbar on hover for IE and Edge */
}

.column-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.column-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.column-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.project-card.sla-warning {
  border: 2px solid #FFA726;
  border-radius: 8px;
}

.project-card.sla-expired {
  border: 2px solid #EF5350;
  border-radius: 8px;
}

.project-card.sla-warning .v-card,
.project-card.sla-expired .v-card {
  border-left: 4px solid;
}

.project-card.sla-warning .v-card {
  border-left-color: #FFA726;
}

.project-card.sla-expired .v-card {
  border-left-color: #EF5350;
}

/* Loading Overlay Styles */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: 8px;
}

.loading-card {
  max-width: 400px;
  width: 90%;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-blur {
  filter: blur(2px);
  pointer-events: none;
  transition: filter 0.3s ease;
}

.kanban-board {
  position: relative;
}

/* Estilos para o menu fixo no canto superior direito */
.project-menu-container {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px;
}
</style> 